import React, { useMemo } from "react";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Row, Col } from "react-grid-system";
import { BackIcon } from "../../svg";
import Button from "../Button";
import Box from "../Box";
import Loader from "../Loader";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridReadyEvent } from "ag-grid-community";
import { getGridDefaultProps } from "constants/agGridConstants";
import { useGetHermesOrderDetails } from "api/reactQuery/queryComponents";
import { BSCoreServicesDtoHermesHermesOrdersHermesOrderProduct } from "api/reactQuery/querySchemas";

const Detail = styled.div`
  position: relative;
`;

const DetailRow = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: 10px 15px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const Label = styled.h6`
  font-size: 15px;
  font-weight: 500;
  width: 40%;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.dark};
`;

const Value = styled.div`
  font-size: 14px;
  font-weight: 400;
  width: 60%;
  text-align: right;
`;

const Buttons = styled.div`
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
`;

const PocOrderDetails: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: details, isLoading } = useGetHermesOrderDetails({
    queryParams: { orderId: Number(orderId) },
  });

  const productColumnDefs = useMemo<
    ColDef<BSCoreServicesDtoHermesHermesOrdersHermesOrderProduct>[]
  >(
    () => [
      { field: "position", headerName: t("position"), maxWidth: 120 },
      { field: "hermesSkuId", headerName: "Hermes ID", maxWidth: 140 },
      { field: "hermesSkuName", headerName: t("hermes_name") },
      { field: "quantity", headerName: t("quantity"), maxWidth: 120 },
      {
        field: "distributorProductCode",
        headerName: t("distributor_product_code"),
      },
      {
        field: "distributorProductName",
        headerName: t("distributor_product_name"),
      },
      {
        field: "isFreeBeer",
        headerName: t("free_beer"),
        maxWidth: 120,
        cellRenderer: (params: { value: boolean }) =>
          params.value ? "TRUE" : "FALSE",
      },
    ],
    [t],
  );

  if (isLoading || !details) {
    return <Loader isLoading={true} />;
  }

  return (
    <div style={{ paddingBottom: 30, marginTop: -20 }}>
      <Buttons>
        <Button
          icon={BackIcon}
          size="large"
          variant="outlined"
          onClick={() => navigate("/poc-orders")}
        >
          {t("back")}
        </Button>
      </Buttons>

      <Box
        hasMargin
        hasContentGutter
        title={`${t("order_number_full")}: ${details.hermesOrderId} | ${t("depot")}: ${details.depotName}`}
      >
        <Detail />
      </Box>

      {details.hermesOrderComment && (
        <Box hasMargin hasContentGutter title={t("order_comment")}>
          <Detail>
            <DetailRow>
              <Value style={{ width: "100%", textAlign: "left" }}>
                {details.hermesOrderComment}
              </Value>
            </DetailRow>
          </Detail>
        </Box>
      )}

      <Row>
        <Col xs={12} sm={6}>
          <Box hasMargin hasContentGutter title={t("hermes_object")}>
            <Detail>
              <DetailRow>
                <Label>{t("hermes_poc_code")}</Label>
                <Value>{details.hermesPocId}</Value>
              </DetailRow>
              <DetailRow>
                <Label>{t("hermes_poc_name")}</Label>
                <Value>{details.hermesPocName}</Value>
              </DetailRow>
              <DetailRow>
                <Label>{t("hermes_poc_address")}</Label>
                <Value>{details.hermesPocAddress}</Value>
              </DetailRow>
              <DetailRow>
                <Label>{t("hermes_poc_client_name")}</Label>
                <Value>{details.hermesCustomer}</Value>
              </DetailRow>
              <DetailRow>
                <Label>{t("hermes_poc_fisc_number")}</Label>
                <Value>{details.hermesVat}</Value>
              </DetailRow>
            </Detail>
          </Box>
        </Col>

        <Col xs={12} sm={6}>
          <Box hasMargin hasContentGutter title={t("distributor_object")}>
            <Detail>
              <DetailRow>
                <Label>{t("distributor_poc_code")}</Label>
                <Value>{details.distributorCustomerCode}</Value>
              </DetailRow>
              <DetailRow>
                <Label>{t("distributor_poc_name")}</Label>
                <Value>{details.distributorCustomerName}</Value>
              </DetailRow>
              <DetailRow>
                <Label>{t("distributor_poc_address")}</Label>
                <Value>{details.distributorCustomerAddress}</Value>
              </DetailRow>
              <DetailRow>
                <Label>{t("distributor_poc_fisc_number")}</Label>
                <Value>{details.distributorCustomerVat}</Value>
              </DetailRow>
            </Detail>
          </Box>
        </Col>
      </Row>

      <Box hasMargin title={t("products_in_order")}>
        <div style={{ padding: "0 12px 12px" }}>
          <AgGridReact
            rowData={details.orderProducts}
            columnDefs={productColumnDefs}
            domLayout="autoHeight"
            onGridReady={(e: GridReadyEvent) => {
              e.api.setGridOption("popupParent", document.body);
            }}
            {...getGridDefaultProps()}
          />
        </div>
      </Box>
    </div>
  );
};

export default PocOrderDetails;
